<template>
  <div />
</template>

<script>

/**
 * Vue: Boost api call (boost-api-call)
 * -----------------------------------------------------------------------------
 * Component to make Boost API calls.
 * https://help.boostcommerce.net/category/668-filter-and-search-apis-app-customization
 *
 * @param {String} buildFilterTree - Leave blank if filters aren't needed or
 * endpoint is 'search/suggest', otherwise value is '&build_filter_tree=true'.
 * @param {String} choices - Formatted filter choices to use in API call.
 * @param {Number} collectionId - Collection id to use for 'filter' endpoint.
 * @param {String} endpoint - Determines which API to use. Options are 'filter'
 * (for collections), 'search' or 'search/suggest' (predictive search).
 * @param {String} eventType - Type of event.
 * @param {Number} limit - Number of products to retrieve per page.
 * @param {Number} page - Current page number.
 * @param {String} query - Search query.
 * @param {String} shop - Permanent shop domain.
 * @param {String} storageKey - Storage key used for temp collection storage.
 *
 */
import productQuery from '~/graphql/queries/boost-data.gql'
import { formatLocalisedPriceData } from '~/helpers/format-boost'

export default {
  name: 'BoostApiCall',

  props: {
    buildFilterTree: {
      type: Boolean,
      default: true,
    },
    choices: {
      type: String,
      default: '',
    },
    collectionId: {
      type: Number,
      default: null,
    },
    endpoint: {
      type: String,
      default: '',
    },
    eventType: {
      type: String,
      default: '',
    },
    limit: {
      type: Number,
      default: 16,
    },
    page: {
      type: Number,
      default: 1,
    },
    query: {
      type: String,
      default: '',
    },
    shop: {
      type: String,
      default: '',
    },
    storageKey: {
      type: String,
      default: 'collection-temp',
    },
  },

  emits: {
    'boost-updated': (payload) => typeof payload === 'object',
    'boost-prices-updated': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      global: true,
    }
  },

  computed: {

    /**
     * Compute shop URL for Boost API.
     * @returns {String}
     */
    shopUrl() {
      return this.shop.replace('//', '').split('/')[0]
    },
  },

  watch: {

    /**
     * Watch choices update to get product results.
     * - Ignore update if products and choices are being set from storage.
     */
    choices(choices) {
      if (sessionStorage.getItem(this.storageKey)) {
        sessionStorage.removeItem(this.storageKey)
        return
      }

      this.getProductResults(choices)
    },

    /**
     * Watch endpoint to get new results.
     */
    endpoint() {
      this.getProductResults(this.choices)
    },

    /**
     * Watch query update to get product results.
     */
    query() {
      this.getProductResults(this.choices)
    },
  },

  methods: {

    /**
     * Format filter tree param for API call.
     * @returns {String}
     */
    getFilters() {
      if (this.endpoint === 'search/suggest' || !this.buildFilterTree) {
        return ''
      }

      return '&build_filter_tree=true'
    },

    /**
     * Check collection ID before making API call.
     * @returns {String}
     */
    getCollectionScope() {
      let collectionScope = `&collection_scope=${this.collectionId}`

      if (this.collectionId === null) {
        collectionScope = ''
      }

      return collectionScope
    },

    /**
     * Check products per page limit before making API call.
     * @returns {String}
     */
    getProductsPerPage() {
      let limit = `&limit=${this.limit}`

      if (isNaN(this.limit)) {
        limit = ''
      }

      return limit
    },

    /**
     * Get and format search query for search/suggest endpoint.
     * @returns {String}
     */
    getQuery() {
      let query = ''

      if (
        [
          'search',
          'search/collections',
          'search/pages',
          'search/suggest',
        ].includes(this.endpoint)
      ) {
        query = `&q=${this.query}&fuzzy=2`
      }

      return query
    },

    /**
     * Get locale.
     * @returns {String}
     */
    getLocale() {
      return `&locale=${cnvs.store.language}`
    },

    /**
     * Get currency.
     * @returns {String}
     */
    getCurrency() {
      return `&currency=${cnvs.store.currency}`
    },

    /**
     * Get vendor.
     * - Only applicable to collections/vendors?q= URLs.
     * @returns {String}
     */
    getVendor() {
      if (cnvs.page.collection !== 'vendors' || cnvs.page.handle) {
        return ''
      }

      return `&vendor=${cnvs.page.title.replaceAll('&amp;', '%26')}`
    },

    /**
     * Get event type.
     * @returns {String}
     */
    getEventType() {
      if (!this.eventType) {
        return ''
      }

      return `&event_type=${this.eventType}`
    },

    /**
     * Get product available.
     * @param {String} choices - Search parameters to add to request url.
     * @returns {String}
     */
    getProductAvailable(choices) {
      if (cnvs.settings.outOfStockProducts === 'hide') {
        return '&variant_available=true'
      }

      /**
       * If hide after filtering then remove page and sort from choices to
       * determine if filters have been applied.
       * - Always hide out of stock products in predictive search.
       */
      if (cnvs.settings.outOfStockProducts === 'hide_after_filtering') {
        if (this.endpoint === 'search/suggest') {
          return '&variant_available=true'
        }

        const ignoredFilters = ['page', 'sort']

        const filteredChoices = choices
          .split('&')
          .filter(Boolean)
          .filter((choice) => {
            return !ignoredFilters.some((filter) => choice.includes(filter))
          })

        if (filteredChoices.length) {
          return '&variant_available=true'
        }
      }

      return ''
    },

    /**
     * Get available sorting.
     * @returns {String}
     */
    getAvailableSort() {
      return cnvs.settings.sortAvailable ? '&sort_first=available' : ''
    },

    /**
     * Get suggestions limit.
     * @returns {String}
     */
    getSuggestionLimit() {
      if (this.endpoint !== 'search/suggest') {
        return ''
      }

      return '&suggestion_limit=4&dym_limit=5'
    },

    /**
     * Handle API call to Boost.
     * @param {String} choices - Search parameters to add to request url.
     */
    async getProductResults(choices) {
      try {
        let formattedChoices = choices

        if (choices[0] !== '&') {
          formattedChoices = `&${formattedChoices}`
        }

        if (this.endpoint === 'search/suggest' && this.query === '') {
          return
        }

        if (!choices) {
          formattedChoices = ''
        }

        /**
         * Calculate new page to load to get action.
         */
        const params = new URLSearchParams(choices)
        let newPage = 1

        if (params.get('page')) {
          newPage = Number(params.get('page'))
        }

        /**
         * Determine query URL.
         */
        let url = `https://services.mybcapps.com/bc-sf-filter/${this.endpoint}?shop=${this.shopUrl}&return_all_currency_fields=false`

        if (
          this.endpoint !== 'search/collections' &&
          this.endpoint !== 'search/pages'
        ) {
          url += this.getFilters()
          url += this.getCollectionScope()
          url += this.getVendor()
          url += formattedChoices
        }

        url += this.getProductAvailable(formattedChoices)
        url += this.getAvailableSort()
        url += this.getSuggestionLimit()
        url += this.getProductsPerPage()
        url += this.getQuery()
        url += this.getLocale()
        url += this.getCurrency()
        url += this.getEventType()

        const response = await fetch(url)
        const data = await response.json()

        /**
         * Emit event.
         * - Do not render prices.
         */
        this.$emit('boost-updated', {
          ...data,
          action: newPage > this.page ? 'append' : 'replace',
          page: newPage,
        })

        /**
         * Emit separate event with update prices.
         */
        if (data.products) {
          const prices = await this.getConvertedProductPrices(data.products)
          this.$emit('boost-prices-updated', prices)
        }

      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     * Convert product price for Shopify markets.
     * - Boost does not handle markets fully.
     * - An extra network call was required to enable market pricing.
     * @param {object} data - Product data.
     * @returns {Promise}
     */
    getConvertedProductPrices(data) {
      return new Promise(async(resolve, reject) => {
        if (!data.length) {
          resolve([])
        }

        const queryString = data.map((product) => {
          return `id:${product.id}`
        }).join(' OR ')

        try {
          const response = await cnvs.Query({
            query: productQuery,
            variables: {
              country: cnvs.store.country,
              language: cnvs.store.language,
              query: queryString,
            },
          })

          if (!response.products?.edges?.length) {
            resolve([])
            return
          }

          const prices = response.products.edges.map((product) => {
            return formatLocalisedPriceData(product)
          })

          resolve(prices)

        } catch (error) {
          reject(error)
        }
      })
    },
  },
}
</script>
